'use client'

import * as React from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'

import { cn } from '@dc-shared-web/lib'
import { faChevronDown } from '@document-crunch/pro-regular-svg-icons'
import { Icon } from '@dc-shared-web/components/base'

const Accordion = AccordionPrimitive.Root

type AccordionItemProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Item
> & {
  applyBorderAndRounded?: boolean
}

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, applyBorderAndRounded = true, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      applyBorderAndRounded && 'tw-border tw-rounded tw-shadow',
      className
    )}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

type AccordionTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
> & {
  iconClassName?: string
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, iconClassName, children, ...props }, ref) => (
  <AccordionPrimitive.Header className='tw-flex'>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'tw-flex tw-flex-1 tw-justify-between tw-py-3 tw-text-sm tw-text-neutral-600 tw-font-medium tw-transition-all [&[data-state=open]>svg]:tw-rotate-180',
        className
      )}
      {...props}
    >
      {children}
      <Icon
        icon={faChevronDown}
        className={cn(
          'tw-h-3 tw-w-3 tw-shrink-0 tw-transition-transform tw-duration-200 tw-ml-2',
          iconClassName
        )}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className='tw-overflow-hidden tw-text-sm tw-transition-all data-[state=closed]:tw-animate-accordion-up data-[state=open]:tw-animate-accordion-down'
    {...props}
  >
    <div className={cn('tw-pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
))

AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
